import React, { useState, useEffect } from "react";
import _ from "lodash";
import { Link } from "react-router-dom";
import { connect } from "react-redux";
import queryString from "query-string";
import LanguageSwitcher from "Jarvis/JarvisComponents/LanguageSwitcher";
import { userSignIn } from "Jarvis/JarvisActions/Auth";
import { notify } from "Jarvis/JarvisComponents/Notification";
import {
  getPrevUsername,
  setCompany,
  setSubSystem,
} from "Jarvis/JarvisServices/JarvisLocalStorageService";
import {
  isNullOrWhiteSpace,
  isTrue,
} from "Jarvis/JarvisServices/JarvisCommonService";
import FormContainer from "./FormContainer";
import ConcurrentLogin from "./ConcurrentLogin";

const ModernStyles = () => (
  <style>{`
    .jv-modern-root {
      position: fixed;
      inset: 0;
      width: 100vw;
      height: 100vh;
      overflow: auto;
      background:
        radial-gradient(900px 500px at 12% 8%, rgba(94, 114, 228, 0.22), transparent 65%),
        radial-gradient(700px 420px at 92% 96%, rgba(17, 205, 239, 0.16), transparent 65%),
        linear-gradient(160deg, #0a1224 0%, #131b35 55%, #0a1020 100%);
      font-family: inherit;
      -webkit-font-smoothing: antialiased;
    }
    /* Static dot pattern removed — the neural canvas now provides texture */
    .jv-modern-root::before { content: none; }
    .jv-modern-shell {
      position: relative;
      z-index: 1;
      min-height: 100vh;
      display: flex;
      flex-direction: row;
      gap: 0;
    }
    .jv-modern-brand {
      flex: 1 1 0;
      transition:
        flex-grow 0.8s cubic-bezier(0.4, 0, 0.2, 1),
        padding 0.8s cubic-bezier(0.4, 0, 0.2, 1);
      position: relative;
      display: flex;
      flex-direction: column;
      justify-content: space-between;
      padding: 56px 28px 48px 64px;
      color: #fff;
      overflow: hidden;
    }
    .jv-modern-shell.is-success .jv-modern-brand {
      flex-grow: 0.45;
      padding: 40px 32px;
      min-width: 280px;
    }
    .jv-modern-shell.is-success .jv-modern-brand-inner {
      transform: translateX(-4px);
    }
    .jv-modern-brand-inner {
      transition: transform 0.8s cubic-bezier(0.4, 0, 0.2, 1);
    }

    /* Pre-login marketing content collapses out of the way when signed in */
    .jv-modern-headline-text,
    .jv-modern-feature-grid,
    .jv-modern-sso {
      transition:
        opacity 0.45s ease,
        transform 0.55s cubic-bezier(0.4, 0, 0.2, 1),
        max-height 0.6s cubic-bezier(0.4, 0, 0.2, 1),
        margin 0.55s ease;
      overflow: hidden;
      max-height: 800px;
    }
    .jv-modern-shell.is-success .jv-modern-headline-text,
    .jv-modern-shell.is-success .jv-modern-feature-grid,
    .jv-modern-shell.is-success .jv-modern-sso {
      opacity: 0;
      max-height: 0;
      margin-top: 0 !important;
      margin-bottom: 0 !important;
      transform: translateX(-24px);
      pointer-events: none;
    }

    /* A small "signed-in" status line that fades in to fill the brand-side space */
    .jv-modern-signed-status {
      margin-top: 22px;
      max-width: 360px;
      opacity: 0;
      transform: translateY(8px);
      transition: opacity 0.5s ease 0.25s, transform 0.5s ease 0.25s;
      display: flex;
      align-items: center;
      gap: 10px;
      font-size: 13px;
      color: rgba(255,255,255,0.85);
    }
    .jv-modern-shell.is-success .jv-modern-signed-status {
      opacity: 1;
      transform: translateY(0);
    }
    .jv-modern-signed-status-dot {
      width: 8px; height: 8px; border-radius: 50%;
      background: #34d399;
      box-shadow: 0 0 12px rgba(52,211,153,0.8), 0 0 0 0 rgba(52,211,153,0.6);
      animation: jv-signed-pulse 1.8s ease-in-out infinite;
      flex-shrink: 0;
    }
    @keyframes jv-signed-pulse {
      0%   { box-shadow: 0 0 0 0 rgba(52,211,153,0.6), 0 0 12px rgba(52,211,153,0.8); }
      70%  { box-shadow: 0 0 0 10px rgba(52,211,153,0), 0 0 12px rgba(52,211,153,0.8); }
      100% { box-shadow: 0 0 0 0 rgba(52,211,153,0), 0 0 12px rgba(52,211,153,0.8); }
    }
    /* Neural-network particle canvas (full-root background) */
    .jv-modern-neural {
      position: absolute;
      inset: 0;
      width: 100%;
      height: 100%;
      display: block;
      z-index: 0;
      pointer-events: none;
    }
    /* Legacy orbital decor — hidden in favor of the neural canvas */
    .jv-modern-decor { display: none; }
    @keyframes jv-decor-spin {
      from { transform: rotate(0deg); }
      to   { transform: rotate(360deg); }
    }
    .jv-modern-brand-overlay {
      position: absolute;
      inset: 0;
      background: linear-gradient(135deg, rgba(10,17,36,0.30) 0%, rgba(10,17,36,0.60) 100%);
    }
    .jv-modern-brand::after {
      content: "";
      position: absolute;
      top: 14%; bottom: 14%; right: 0;
      width: 1px;
      background: linear-gradient(180deg,
        transparent 0%,
        rgba(255,255,255,0.10) 35%,
        rgba(255,255,255,0.10) 65%,
        transparent 100%);
      pointer-events: none;
      z-index: 2;
    }
    .jv-modern-brand-inner {
      position: relative;
      z-index: 1;
      display: flex;
      flex-direction: column;
      justify-content: space-between;
      height: 100%;
      gap: 24px;
    }
    .jv-modern-brand-top {
      display: flex;
      align-items: center;
      gap: 14px;
    }
    .jv-modern-wordmark-link {
      text-decoration: none;
      display: inline-flex;
      align-items: center;
      gap: 14px;
      color: inherit;
      position: relative;
    }
    .jv-modern-wordmark-link:hover { text-decoration: none; }
    .jv-modern-wordmark-text {
      display: inline-flex;
      flex-direction: column;
      position: relative;
    }
    .jv-modern-wordmark {
      font-family: 'Segoe UI', 'Inter', system-ui, -apple-system, sans-serif;
      font-size: clamp(40px, 4.4vw, 56px);
      line-height: 1;
      font-weight: 900;
      letter-spacing: -0.02em;
      background: linear-gradient(120deg, #ffffff 0%, #c9d6ff 30%, #11cdef 55%, #c9d6ff 80%, #ffffff 100%);
      background-size: 250% 100%;
      -webkit-background-clip: text;
      background-clip: text;
      color: transparent;
      text-shadow: 0 4px 30px rgba(94,114,228,0.35);
      position: relative;
      display: inline-block;
      transform-origin: left center;
      animation:
        jv-wordmark-enter 0.9s cubic-bezier(0.16, 1, 0.3, 1) both,
        jv-wordmark-shimmer 6s ease-in-out 1s infinite,
        jv-wordmark-float 6s ease-in-out 1s infinite;
    }
    .jv-modern-wordmark::after {
      content: "";
      position: absolute;
      left: 0; right: 0; bottom: -6px;
      height: 2px;
      border-radius: 2px;
      background: linear-gradient(90deg, transparent 0%, rgba(94,114,228,0.9) 40%, rgba(17,205,239,0.9) 60%, transparent 100%);
      background-size: 220% 100%;
      opacity: 0.0;
      animation: jv-wordmark-underline 4s ease-in-out 1.5s infinite;
    }
    @keyframes jv-wordmark-enter {
      0%   { opacity: 0; transform: translateY(10px) scale(0.94); letter-spacing: 0.06em; filter: blur(4px); }
      60%  { opacity: 1; filter: blur(0); }
      100% { opacity: 1; transform: translateY(0) scale(1); letter-spacing: -0.02em; filter: blur(0); }
    }
    @keyframes jv-wordmark-shimmer {
      0%   { background-position: 0% 50%; }
      50%  { background-position: 100% 50%; }
      100% { background-position: 0% 50%; }
    }
    @keyframes jv-wordmark-float {
      0%, 100% { transform: translateY(0); }
      50%      { transform: translateY(-3px); }
    }
    @keyframes jv-wordmark-underline {
      0%   { opacity: 0; background-position: -30% 0; }
      30%  { opacity: 0.9; }
      70%  { opacity: 0.9; }
      100% { opacity: 0; background-position: 130% 0; }
    }
    /* Legacy dot — kept hidden; replaced by AI emblem */
    .jv-modern-wordmark-dot { display: none; }

    /* AI Emblem (replaces old pulsing dot) */
    .jv-ai-emblem {
      display: inline-flex;
      flex-shrink: 0;
      line-height: 0;
      animation:
        jv-emblem-enter 0.8s cubic-bezier(0.16, 1, 0.3, 1) both,
        jv-emblem-aura 3.4s ease-in-out 0.6s infinite;
    }
    .jv-ai-orbit {
      transform-origin: 32px 32px;
      animation: jv-ai-spin 22s linear infinite;
    }
    .jv-ai-spokes {
      transform-origin: 32px 32px;
      animation: jv-ai-spin-rev 60s linear infinite;
    }
    @keyframes jv-ai-spin {
      from { transform: rotate(0deg); }
      to   { transform: rotate(360deg); }
    }
    @keyframes jv-ai-spin-rev {
      from { transform: rotate(0deg); }
      to   { transform: rotate(-360deg); }
    }
    .jv-ai-hub {
      transform-origin: 32px 32px;
      animation: jv-ai-hub-pulse 2.4s ease-in-out infinite;
    }
    .jv-ai-hub-core {
      transform-origin: 32px 32px;
      animation: jv-ai-hub-core 2.4s ease-in-out infinite;
    }
    @keyframes jv-ai-hub-pulse {
      0%, 100% { transform: scale(1); }
      50%      { transform: scale(1.18); }
    }
    @keyframes jv-ai-hub-core {
      0%, 100% { opacity: 0.9; transform: scale(1); }
      50%      { opacity: 1;   transform: scale(0.75); }
    }
    .jv-ai-node {
      transform-origin: 32px 32px;
      animation: jv-ai-node-flicker 3.6s ease-in-out infinite;
    }
    .jv-ai-node-0 { animation-delay: 0s; }
    .jv-ai-node-1 { animation-delay: 0.4s; }
    .jv-ai-node-2 { animation-delay: 0.8s; }
    .jv-ai-node-3 { animation-delay: 1.2s; }
    .jv-ai-node-4 { animation-delay: 1.6s; }
    .jv-ai-node-5 { animation-delay: 2.0s; }
    @keyframes jv-ai-node-flicker {
      0%, 100% { opacity: 0.55; }
      50%      { opacity: 1; }
    }
    @keyframes jv-emblem-enter {
      from { opacity: 0; transform: scale(0.6) rotate(-20deg); }
      to   { opacity: 1; transform: scale(1) rotate(0deg); }
    }
    @keyframes jv-emblem-aura {
      0%, 100% { filter: drop-shadow(0 0 12px rgba(94,114,228,0.45)); }
      50%      { filter: drop-shadow(0 0 22px rgba(17,205,239,0.75)); }
    }

    /* JARVIS + AI pill on the same row */
    .jv-modern-wordmark-row {
      display: inline-flex;
      align-items: flex-start;
      gap: 10px;
      line-height: 1;
    }
    .jv-ai-badge {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      padding: 3px 9px;
      border-radius: 999px;
      font-size: 10px;
      font-weight: 800;
      letter-spacing: 0.18em;
      color: #0a1124;
      background: linear-gradient(135deg, #d9e2ff 0%, #b6e6ff 100%);
      box-shadow:
        0 6px 14px rgba(94,114,228,0.35),
        inset 0 0 0 1px rgba(255,255,255,0.6);
      margin-top: 4px;
      opacity: 0;
      transform: translateY(-4px) scale(0.9);
      animation: jv-ai-badge-enter 0.6s cubic-bezier(0.16, 1, 0.3, 1) 0.7s forwards;
      position: relative;
      overflow: hidden;
    }
    .jv-ai-badge::after {
      content: "";
      position: absolute;
      inset: 0;
      background: linear-gradient(120deg,
        transparent 30%,
        rgba(255,255,255,0.7) 50%,
        transparent 70%);
      transform: translateX(-100%);
      animation: jv-ai-badge-shine 4.5s ease-in-out 2s infinite;
    }
    @keyframes jv-ai-badge-enter {
      to { opacity: 1; transform: translateY(0) scale(1); }
    }
    @keyframes jv-ai-badge-shine {
      0%   { transform: translateX(-100%); }
      45%  { transform: translateX(100%); }
      100% { transform: translateX(100%); }
    }

    /* Hover: emblem rotates faster & glows brighter */
    .jv-modern-wordmark-link:hover .jv-modern-wordmark {
      animation-play-state: paused, paused, paused;
      transform: scale(1.04);
      filter: drop-shadow(0 0 14px rgba(94,114,228,0.5));
      transition: transform 0.25s ease, filter 0.25s ease;
    }
    .jv-modern-wordmark-link:hover .jv-ai-emblem {
      filter: drop-shadow(0 0 26px rgba(17,205,239,0.9));
      transition: filter 0.3s ease;
    }
    .jv-modern-wordmark-link:hover .jv-ai-orbit {
      animation-duration: 6s;
    }
    .jv-modern-wordmark-link:hover .jv-ai-badge {
      transform: translateY(-1px) scale(1.05);
      transition: transform 0.2s ease;
    }
    .jv-modern-wordmark-company {
      margin-top: 6px;
      font-size: 13px;
      font-weight: 600;
      letter-spacing: 0.16em;
      text-transform: uppercase;
      color: rgba(255,255,255,0.78);
      opacity: 0;
      animation: jv-wordmark-company-enter 0.6s ease-out 0.6s forwards;
    }
    @keyframes jv-wordmark-company-enter {
      from { opacity: 0; transform: translateY(4px); letter-spacing: 0.30em; }
      to   { opacity: 1; transform: translateY(0); letter-spacing: 0.16em; }
    }
    /* On successful login, give the wordmark a brief celebratory pulse */
    .jv-modern-root.is-success .jv-modern-wordmark {
      animation:
        jv-wordmark-shimmer 1.6s ease-in-out 1,
        jv-wordmark-float 6s ease-in-out infinite;
    }
    .jv-modern-headline h1 {
      font-size: clamp(28px, 3.2vw, 44px);
      line-height: 1.1;
      margin: 0 0 14px;
      font-weight: 700;
      background: linear-gradient(90deg, #fff 0%, #b6c7ff 100%);
      -webkit-background-clip: text;
      background-clip: text;
      color: transparent;
    }
    .jv-modern-headline p {
      max-width: 460px;
      color: rgba(255,255,255,0.78);
      font-size: 15px;
      line-height: 1.6;
      margin: 0;
    }
    .jv-modern-feature-grid {
      display: grid;
      grid-template-columns: repeat(2, minmax(0, 1fr));
      gap: 8px;
      margin-top: 24px;
      max-width: 480px;
    }
    .jv-modern-feature {
      display: flex;
      align-items: flex-start;
      gap: 8px;
      padding: 10px 12px;
      border-radius: 10px;
      background: rgba(255,255,255,0.04);
      border: 1px solid rgba(255,255,255,0.07);
    }
    .jv-modern-feature-dot {
      width: 6px; height: 6px; border-radius: 50%;
      background: linear-gradient(135deg, #5e72e4, #11cdef);
      margin-top: 6px;
      flex-shrink: 0;
    }
    .jv-modern-feature-text {
      font-size: 12.5px;
      color: rgba(255,255,255,0.82);
      line-height: 1.4;
    }
    .jv-modern-brand-footer {
      font-size: 12px;
      color: rgba(255,255,255,0.5);
    }

    /* SSO callout in brand pane */
    .jv-modern-sso {
      margin-top: 22px;
      display: flex;
      gap: 12px;
      align-items: flex-start;
      max-width: 480px;
      padding: 12px 14px;
      border-radius: 12px;
      background: rgba(94,114,228,0.10);
      border: 1px solid rgba(255,255,255,0.10);
      backdrop-filter: blur(8px);
    }
    .jv-modern-sso-icon {
      flex-shrink: 0;
      width: 32px;
      height: 32px;
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: 8px;
      background: linear-gradient(135deg, rgba(94,114,228,0.7), rgba(17,205,239,0.5));
      color: #fff;
    }
    .jv-modern-sso-title {
      font-size: 13px;
      font-weight: 600;
      color: #fff;
      margin-bottom: 2px;
      letter-spacing: 0.01em;
    }
    .jv-modern-sso-text {
      font-size: 12px;
      color: rgba(255,255,255,0.7);
      line-height: 1.5;
      margin-bottom: 8px;
    }
    .jv-modern-sso-tags {
      display: flex;
      flex-wrap: wrap;
      gap: 6px;
    }
    .jv-modern-sso-tags span {
      font-size: 11px;
      font-weight: 600;
      letter-spacing: 0.04em;
      padding: 3px 9px;
      border-radius: 999px;
      background: rgba(255,255,255,0.10);
      color: rgba(255,255,255,0.92);
      border: 1px solid rgba(255,255,255,0.18);
    }

    /* Shield + secure badge + trust strip + legal */
    .jv-modern-card-shield {
      display: flex;
      justify-content: center;
      margin-bottom: 6px;
    }
    .jv-modern-secure-badge {
      display: inline-flex;
      align-items: center;
      gap: 5px;
      margin-top: 10px;
      padding: 3px 9px;
      border-radius: 999px;
      background: #ecfdf5;
      color: #047857;
      font-size: 11px;
      font-weight: 600;
      letter-spacing: 0.02em;
      border: 1px solid #a7f3d0;
    }
    .jv-modern-trust {
      margin-top: 20px;
      padding-top: 16px;
      border-top: 1px solid #f1f5f9;
      display: flex;
      flex-wrap: wrap;
      gap: 6px 10px;
      align-items: center;
      justify-content: center;
    }
    .jv-modern-trust-badge {
      display: inline-flex;
      align-items: baseline;
      gap: 4px;
      padding: 4px 9px;
      border-radius: 6px;
      background: #f8fafc;
      border: 1px solid #eef2f7;
    }
    .jv-modern-trust-label {
      font-size: 10.5px;
      font-weight: 700;
      color: #334155;
      letter-spacing: 0.04em;
    }
    .jv-modern-trust-sub {
      font-size: 10px;
      color: #94a3b8;
      letter-spacing: 0.02em;
    }
    .jv-modern-legal {
      margin-top: 12px;
      font-size: 10.5px;
      color: #94a3b8;
      text-align: center;
      line-height: 1.5;
    }

    .jv-modern-form-pane {
      flex: 1 1 0;
      transition: flex-grow 0.8s cubic-bezier(0.4, 0, 0.2, 1);
      display: flex;
      align-items: center;
      justify-content: flex-start;
      padding: 56px 48px 56px 12px;
    }
    .jv-modern-shell.is-success .jv-modern-form-pane {
      flex-grow: 1.55;
      justify-content: center;
      padding: 56px 32px;
    }

    /* Card swap: form -> user-info slide-in from the right */
    .jv-anim-slide-in-right {
      animation: jv-slide-in-right 0.7s cubic-bezier(0.16, 1, 0.3, 1) both !important;
    }
    @keyframes jv-slide-in-right {
      0%   { opacity: 0; transform: translateX(60px) scale(0.97); }
      60%  { opacity: 1; }
      100% { opacity: 1; transform: translateX(0) scale(1); }
    }
    .jv-modern-card {
      width: 100%;
      max-width: 420px;
      background: #ffffff;
      border-radius: 20px;
      padding: 36px 34px;
      box-shadow:
        0 36px 72px -28px rgba(0,0,0,0.60),
        0 12px 24px -12px rgba(0,0,0,0.22),
        inset 0 0 0 1px rgba(15,23,42,0.04);
      animation: jv-fade-up 0.55s cubic-bezier(0.16, 1, 0.3, 1);
    }
    @keyframes jv-fade-up {
      from { opacity: 0; transform: translateY(16px); }
      to   { opacity: 1; transform: translateY(0); }
    }
    .jv-modern-card-head { margin-bottom: 22px; text-align: left; }
    .jv-modern-card-head .jv-modern-lang { display: flex; justify-content: flex-end; margin-bottom: 8px; }
    .jv-modern-card-headline {
      display: flex;
      align-items: center;
      gap: 12px;
      margin: 4px 0 6px;
    }
    .jv-modern-card-title {
      font-size: 26px;
      font-weight: 800;
      color: #0f172a;
      margin: 0;
      letter-spacing: -0.02em;
      line-height: 1.1;
    }
    .jv-modern-card-subtitle {
      font-size: 13.5px;
      color: #64748b;
      margin: 4px 0 0;
      line-height: 1.5;
    }

    .jv-modern-card .MuiFormControl-root { width: 100%; }
    .jv-modern-card fieldset { border: 0; padding: 0; margin: 0 0 12px; }
    .jv-modern-card .MuiInputBase-root { border-radius: 12px; }
    .jv-modern-card .MuiOutlinedInput-root fieldset {
      border-color: #e2e8f0;
      transition: border-color 0.2s, box-shadow 0.2s;
    }
    .jv-modern-card .MuiOutlinedInput-root:hover fieldset { border-color: #94a3b8; }
    .jv-modern-card .MuiOutlinedInput-root.Mui-focused fieldset {
      border-color: #5e72e4;
      box-shadow: 0 0 0 4px rgba(94,114,228,0.12);
    }
    .jv-modern-card .MuiButton-containedPrimary {
      background: linear-gradient(135deg, #5e72e4 0%, #825ee4 100%) !important;
      border-radius: 12px !important;
      padding: 10px 24px !important;
      font-weight: 600 !important;
      letter-spacing: 0.02em !important;
      text-transform: none !important;
      box-shadow: 0 8px 20px rgba(94,114,228,0.35) !important;
      transition: transform 0.15s, box-shadow 0.15s !important;
    }
    .jv-modern-card .MuiButton-containedPrimary:hover {
      transform: translateY(-1px);
      box-shadow: 0 12px 26px rgba(94,114,228,0.45) !important;
    }
    .jv-modern-card a { color: #5e72e4; }
    .jv-modern-card a:hover { color: #4256c7; }

    .jv-modern-card.is-wide { max-width: 600px; }
    .jv-modern-shell.is-success .jv-modern-card {
      max-width: 760px;
      padding: 48px 44px;
    }

    /* Success-state: enlarge the user-info elements */
    .jv-modern-shell.is-success .jv-modern-avatar {
      width: 128px;
      height: 128px;
      padding: 5px;
      box-shadow: 0 14px 32px rgba(94,114,228,0.40);
    }
    .jv-modern-shell.is-success .jv-modern-welcome {
      font-size: 14px;
      letter-spacing: 0.1em;
      margin-top: 22px;
    }
    .jv-modern-shell.is-success .jv-modern-fullname {
      font-size: 30px;
      font-weight: 800;
      letter-spacing: -0.01em;
      margin: 6px 0 4px;
    }
    .jv-modern-shell.is-success .jv-modern-description {
      font-size: 14.5px;
      margin-bottom: 28px;
    }
    .jv-modern-shell.is-success .jv-modern-info-grid {
      gap: 14px;
      margin-bottom: 28px;
    }
    .jv-modern-shell.is-success .jv-modern-info-tile {
      padding: 14px 18px;
      border-radius: 14px;
    }
    .jv-modern-shell.is-success .jv-modern-info-label {
      font-size: 12px;
      margin-bottom: 6px;
    }
    .jv-modern-shell.is-success .jv-modern-info-value {
      font-size: 15px;
      font-weight: 600;
    }
    .jv-modern-shell.is-success .jv-modern-warn {
      font-size: 14px;
      padding: 12px 18px;
      margin-bottom: 22px;
    }
    .jv-modern-shell.is-success .jv-modern-continue {
      font-size: 17px;
      padding: 16px 28px;
      letter-spacing: 0.03em;
      border-radius: 14px;
    }

    /* Modern user-info card */
    .jv-modern-userinfo { display: flex; flex-direction: column; align-items: center; }
    .jv-modern-avatar {
      width: 96px; height: 96px;
      border-radius: 50%;
      padding: 4px;
      background: linear-gradient(135deg, #5e72e4, #11cdef);
      box-shadow: 0 10px 24px rgba(94,114,228,0.35);
    }
    .jv-modern-avatar img {
      width: 100%; height: 100%; border-radius: 50%;
      object-fit: cover;
      background: #fff;
    }
    .jv-modern-welcome {
      font-size: 13px;
      color: #64748b;
      text-transform: uppercase;
      letter-spacing: 0.08em;
      margin-top: 18px;
    }
    .jv-modern-fullname {
      font-size: 22px;
      font-weight: 700;
      color: #0f172a;
      margin: 4px 0 2px;
      text-align: center;
    }
    .jv-modern-description {
      font-size: 13px;
      color: #64748b;
      margin: 0 0 22px;
      text-align: center;
    }
    .jv-modern-info-grid {
      width: 100%;
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 10px;
      margin-bottom: 22px;
    }
    .jv-modern-info-tile {
      background: #f8fafc;
      border: 1px solid #e2e8f0;
      border-radius: 12px;
      padding: 10px 12px;
    }
    .jv-modern-info-label {
      font-size: 11px;
      text-transform: uppercase;
      letter-spacing: 0.06em;
      color: #94a3b8;
      margin-bottom: 4px;
    }
    .jv-modern-info-value {
      font-size: 13px;
      color: #0f172a;
      font-weight: 500;
      word-break: break-word;
    }
    .jv-modern-info-value.ltr { direction: ltr; unicode-bidi: embed; }
    .jv-modern-warn {
      width: 100%;
      background: linear-gradient(135deg, #fff7ed 0%, #fef3c7 100%);
      border: 1px solid #fcd34d;
      color: #92400e;
      border-radius: 12px;
      padding: 10px 14px;
      font-size: 13px;
      margin-bottom: 18px;
    }
    .jv-modern-continue {
      width: 100%;
      border: 0;
      cursor: pointer;
      background: linear-gradient(135deg, #5e72e4 0%, #825ee4 100%);
      color: #fff;
      font-weight: 600;
      letter-spacing: 0.02em;
      font-size: 15px;
      padding: 12px 20px;
      border-radius: 12px;
      box-shadow: 0 10px 24px rgba(94,114,228,0.35);
      transition: transform 0.15s, box-shadow 0.15s;
    }
    .jv-modern-continue:hover {
      transform: translateY(-1px);
      box-shadow: 0 14px 28px rgba(94,114,228,0.45);
    }

    /* Design switcher pill */
    .jv-design-switch {
      position: fixed;
      top: 16px;
      right: 16px;
      z-index: 50;
      display: inline-flex;
      align-items: center;
      gap: 6px;
      padding: 6px;
      border-radius: 999px;
      background: rgba(15, 23, 42, 0.55);
      backdrop-filter: blur(10px);
      border: 1px solid rgba(255,255,255,0.18);
      box-shadow: 0 6px 20px rgba(0,0,0,0.25);
    }
    .jv-design-switch button {
      appearance: none;
      border: 0;
      background: transparent;
      color: rgba(255,255,255,0.75);
      font-size: 12px;
      font-weight: 600;
      padding: 6px 12px;
      border-radius: 999px;
      cursor: pointer;
      letter-spacing: 0.03em;
      transition: background 0.2s, color 0.2s;
    }
    .jv-design-switch button.is-active {
      background: linear-gradient(135deg, #5e72e4, #825ee4);
      color: #fff;
      box-shadow: 0 4px 12px rgba(94,114,228,0.45);
    }
    .jv-design-switch button:not(.is-active):hover {
      color: #fff;
      background: rgba(255,255,255,0.08);
    }

    @media (max-width: 960px) {
      .jv-modern-shell { flex-direction: column; }
      .jv-modern-brand,
      .jv-modern-shell.is-success .jv-modern-brand,
      .jv-modern-form-pane,
      .jv-modern-shell.is-success .jv-modern-form-pane { flex: 1 1 auto; }
      .jv-modern-shell.is-success .jv-modern-brand-inner { transform: none; }
      .jv-modern-brand { padding: 28px 24px 20px; min-height: auto; }
      .jv-modern-headline h1 { font-size: 26px; }
      .jv-modern-headline p { font-size: 14px; }
      .jv-modern-feature-grid { display: none; }
      .jv-modern-sso { display: none; }
      .jv-modern-brand-footer { display: none; }
      .jv-modern-form-pane { padding: 16px 16px 32px; }
      .jv-modern-card { padding: 28px 22px; border-radius: 18px; }
    }

    @media (max-width: 480px) {
      .jv-modern-brand { padding: 22px 18px 14px; }
      .jv-modern-wordmark { font-size: 38px; }
      .jv-ai-emblem svg { width: 44px; height: 44px; }
      .jv-modern-wordmark-link { gap: 10px; }
      .jv-modern-wordmark-company { font-size: 11px; margin-top: 4px; }
      .jv-ai-badge { font-size: 9px; padding: 2px 7px; }
      .jv-modern-headline h1 { font-size: 22px; margin-bottom: 8px; }
      .jv-modern-headline p { font-size: 13px; }
      .jv-modern-card { padding: 24px 18px; border-radius: 16px; }
      .jv-modern-card-title { font-size: 19px; }
      .jv-modern-info-grid { grid-template-columns: 1fr; }
      .jv-modern-avatar { width: 80px; height: 80px; }
      .jv-modern-trust { grid-template-columns: repeat(2, 1fr); }
      .jv-design-switch { top: 10px; right: 10px; }
      .jv-design-switch button { padding: 5px 10px; font-size: 11px; }
    }

    /* Respect users who prefer reduced motion */
    @media (prefers-reduced-motion: reduce) {
      .jv-modern-wordmark,
      .jv-modern-wordmark-dot,
      .jv-modern-wordmark::after,
      .jv-modern-decor,
      .jv-ai-emblem,
      .jv-ai-orbit,
      .jv-ai-spokes,
      .jv-ai-hub,
      .jv-ai-hub-core,
      .jv-ai-node,
      .jv-ai-badge,
      .jv-ai-badge::after,
      .jv-anim-slide-in-right { animation: none !important; }
      .jv-modern-shell,
      .jv-modern-brand,
      .jv-modern-form-pane,
      .jv-modern-brand-inner { transition: none !important; }
    }
  `}</style>
);

const FeatureList = ({ langData }) => {
  const items = [
    _.get(langData, "SecAuthentication_Feature1") ||
      "Enterprise Single Sign-On (SAML 2.0 & OIDC)",
    _.get(langData, "SecAuthentication_Feature2") || "Two-factor authentication",
    _.get(langData, "SecAuthentication_Feature3") || "End-to-end encrypted session",
    _.get(langData, "SecAuthentication_Feature4") ||
      "Role-based access & audit logging",
    _.get(langData, "SecAuthentication_Feature5") ||
      "Active Directory / Entra ID sync",
    _.get(langData, "SecAuthentication_Feature6") || "Mobile friendly experience",
  ];
  return (
    <div className="jv-modern-feature-grid">
      {items.map((t, i) => (
        <div className="jv-modern-feature" key={i}>
          <div className="jv-modern-feature-dot" />
          <div className="jv-modern-feature-text">{t}</div>
        </div>
      ))}
    </div>
  );
};

const SsoCallout = ({ langData }) => (
  <div className="jv-modern-sso">
    <div className="jv-modern-sso-icon" aria-hidden="true">
      <svg viewBox="0 0 24 24" width="20" height="20" fill="none">
        <path
          d="M4 12h6M14 12h6M12 4v6M12 14v6"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
        />
        <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2" />
      </svg>
    </div>
    <div className="jv-modern-sso-body">
      <div className="jv-modern-sso-title">
        {_.get(langData, "SecAuthentication_SsoTitle") ||
          "Single Sign-On ready"}
      </div>
      <div className="jv-modern-sso-text">
        {_.get(langData, "SecAuthentication_SsoText") ||
          "Connect through your corporate identity provider — SAML 2.0, OIDC, Azure AD / Entra ID, Okta, Google Workspace and Active Directory."}
      </div>
      <div className="jv-modern-sso-tags">
        <span>SAML 2.0</span>
        <span>OIDC</span>
        <span>Entra ID</span>
        <span>Okta</span>
        <span>AD FS</span>
      </div>
    </div>
  </div>
);

// AI emblem — a stylized neural hub: outer aura, dashed orbit ring, six
// satellite nodes connected to a glowing central hub. The orbit ring rotates
// slowly; the hub pulses. Built entirely from SVG so it scales cleanly.
const AiEmblem = () => {
  // 6 satellite positions on a circle of radius 18 around (32, 32)
  const cx = 32;
  const cy = 32;
  const r = 18;
  const sats = Array.from({ length: 6 }, (_, i) => {
    const a = (Math.PI / 3) * i - Math.PI / 2;
    return { x: cx + r * Math.cos(a), y: cy + r * Math.sin(a) };
  });
  return (
    <span className="jv-ai-emblem" aria-hidden="true">
      <svg viewBox="0 0 64 64" width="56" height="56">
        <defs>
          <linearGradient id="jvAiRing" x1="0" x2="1" y1="0" y2="1">
            <stop offset="0%" stopColor="#5e72e4" />
            <stop offset="100%" stopColor="#11cdef" />
          </linearGradient>
          <radialGradient id="jvAiAura" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#5e72e4" stopOpacity="0.55" />
            <stop offset="60%" stopColor="#5e72e4" stopOpacity="0.12" />
            <stop offset="100%" stopColor="#5e72e4" stopOpacity="0" />
          </radialGradient>
          <radialGradient id="jvAiHubGrad" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#ffffff" />
            <stop offset="55%" stopColor="#11cdef" />
            <stop offset="100%" stopColor="#5e72e4" />
          </radialGradient>
        </defs>
        {/* Outer aura */}
        <circle cx={cx} cy={cy} r="30" fill="url(#jvAiAura)" />
        {/* Outer solid ring */}
        <circle
          cx={cx}
          cy={cy}
          r="27"
          fill="none"
          stroke="url(#jvAiRing)"
          strokeWidth="1.2"
          opacity="0.65"
        />
        {/* Dashed inner orbit (rotates) */}
        <g className="jv-ai-orbit">
          <circle
            cx={cx}
            cy={cy}
            r="22"
            fill="none"
            stroke="rgba(255,255,255,0.45)"
            strokeWidth="0.8"
            strokeDasharray="2 4"
          />
        </g>
        {/* Connections + satellites (counter-rotates slowly) */}
        <g className="jv-ai-spokes">
          {sats.map((s, i) => (
            <line
              key={"l" + i}
              x1={cx}
              y1={cy}
              x2={s.x}
              y2={s.y}
              stroke="url(#jvAiRing)"
              strokeWidth="1"
              opacity="0.55"
            />
          ))}
          {sats.map((s, i) => (
            <circle
              key={"n" + i}
              cx={s.x}
              cy={s.y}
              r="2.2"
              fill={i % 2 === 0 ? "#11cdef" : "#5e72e4"}
              className={"jv-ai-node jv-ai-node-" + i}
            />
          ))}
        </g>
        {/* Central hub */}
        <circle
          cx={cx}
          cy={cy}
          r="5.2"
          fill="url(#jvAiHubGrad)"
          className="jv-ai-hub"
        />
        <circle
          cx={cx}
          cy={cy}
          r="2"
          fill="#fff"
          opacity="0.9"
          className="jv-ai-hub-core"
        />
      </svg>
    </span>
  );
};

const ShieldIcon = ({ size = 22 }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} aria-hidden="true">
    <defs>
      <linearGradient id="jvShieldGrad" x1="0" x2="1" y1="0" y2="1">
        <stop offset="0%" stopColor="#5e72e4" />
        <stop offset="100%" stopColor="#11cdef" />
      </linearGradient>
    </defs>
    <path
      d="M12 2l8 3v6.5c0 4.7-3.3 9-8 10.5-4.7-1.5-8-5.8-8-10.5V5l8-3z"
      fill="url(#jvShieldGrad)"
    />
    <path
      d="M8.5 12.5l2.4 2.4 4.6-4.6"
      stroke="#fff"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      fill="none"
    />
  </svg>
);

const TrustStrip = ({ langData }) => {
  const badges = [
    { label: "TLS 1.3", sub: _.get(langData, "SecAuthentication_Trust_Tls") || "encrypted" },
    { label: "SOC 2", sub: _.get(langData, "SecAuthentication_Trust_Soc2") || "Type II" },
    { label: "ISO 27001", sub: _.get(langData, "SecAuthentication_Trust_Iso") || "certified" },
  ];
  return (
    <div className="jv-modern-trust">
      {badges.map((b, i) => (
        <div className="jv-modern-trust-badge" key={i}>
          <span className="jv-modern-trust-label">{b.label}</span>
          <span className="jv-modern-trust-sub">{b.sub}</span>
        </div>
      ))}
    </div>
  );
};

const SecureBadge = ({ langData }) => (
  <div className="jv-modern-secure-badge" title="Secure connection">
    <svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true">
      <path
        d="M6 10V8a6 6 0 1112 0v2"
        stroke="currentColor"
        strokeWidth="2"
        fill="none"
        strokeLinecap="round"
      />
      <rect
        x="4"
        y="10"
        width="16"
        height="11"
        rx="2"
        stroke="currentColor"
        strokeWidth="2"
        fill="none"
      />
      <circle cx="12" cy="15" r="1.6" fill="currentColor" />
    </svg>
    <span>
      {_.get(langData, "SecAuthentication_SecureConnection") ||
        "Secure connection • TLS encrypted"}
    </span>
  </div>
);

/* Self-contained neural-network particle background.
   Renders a canvas of slowly-drifting nodes with lines drawn between
   nearby pairs, plus a "halo" of lines from the cursor to nearby nodes.
   Respects prefers-reduced-motion. */
const NeuralBackground = React.memo(function NeuralBackground() {
  const canvasRef = React.useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    const ctx = canvas.getContext("2d");
    if (!ctx) return undefined;

    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    let width = 0;
    let height = 0;
    let nodes = [];
    let raf = 0;
    const mouse = { x: -9999, y: -9999, active: false };

    const targetCount = () => {
      const area = width * height;
      return Math.min(140, Math.max(50, Math.round(area / 11000)));
    };

    const seedNode = () => ({
      x: Math.random() * width,
      y: Math.random() * height,
      vx: (Math.random() - 0.5) * 0.28,
      vy: (Math.random() - 0.5) * 0.28,
      r: Math.random() * 1.4 + 0.6,
    });

    const resize = () => {
      width = canvas.clientWidth;
      height = canvas.clientHeight;
      canvas.width = Math.floor(width * dpr);
      canvas.height = Math.floor(height * dpr);
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.scale(dpr, dpr);
      const desired = targetCount();
      if (nodes.length < desired) {
        for (let i = nodes.length; i < desired; i++) nodes.push(seedNode());
      } else if (nodes.length > desired) {
        nodes.length = desired;
      }
    };

    const onResize = () => resize();
    const onMove = (e) => {
      const rect = canvas.getBoundingClientRect();
      mouse.x = e.clientX - rect.left;
      mouse.y = e.clientY - rect.top;
      mouse.active = true;
    };
    const onLeave = () => {
      mouse.x = -9999;
      mouse.y = -9999;
      mouse.active = false;
    };

    resize();
    window.addEventListener("resize", onResize);
    // Listen on the document so events reach us even though the canvas
    // sits behind the layout shell (pointer-events: none).
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseleave", onLeave);

    const LINK_DIST = 150;
    const MOUSE_DIST = 200;
    const REPEL_DIST = 110;

    const draw = () => {
      ctx.clearRect(0, 0, width, height);

      // Update positions
      for (let i = 0; i < nodes.length; i++) {
        const n = nodes[i];
        n.x += n.vx;
        n.y += n.vy;
        if (n.x < 0) { n.x = 0; n.vx *= -1; }
        else if (n.x > width) { n.x = width; n.vx *= -1; }
        if (n.y < 0) { n.y = 0; n.vy *= -1; }
        else if (n.y > height) { n.y = height; n.vy *= -1; }

        if (mouse.active) {
          const dx = n.x - mouse.x;
          const dy = n.y - mouse.y;
          const d2 = dx * dx + dy * dy;
          if (d2 < REPEL_DIST * REPEL_DIST && d2 > 1) {
            const d = Math.sqrt(d2);
            const force = (1 - d / REPEL_DIST) * 0.7;
            n.x += (dx / d) * force;
            n.y += (dy / d) * force;
          }
        }
      }

      // Lines between nearby nodes
      ctx.lineWidth = 1;
      for (let i = 0; i < nodes.length; i++) {
        const a = nodes[i];
        for (let j = i + 1; j < nodes.length; j++) {
          const b = nodes[j];
          const dx = a.x - b.x;
          const dy = a.y - b.y;
          const d2 = dx * dx + dy * dy;
          if (d2 < LINK_DIST * LINK_DIST) {
            const alpha = (1 - Math.sqrt(d2) / LINK_DIST) * 0.30;
            ctx.strokeStyle = "rgba(116,138,235," + alpha.toFixed(3) + ")";
            ctx.beginPath();
            ctx.moveTo(a.x, a.y);
            ctx.lineTo(b.x, b.y);
            ctx.stroke();
          }
        }
        if (mouse.active) {
          const dx = a.x - mouse.x;
          const dy = a.y - mouse.y;
          const d2 = dx * dx + dy * dy;
          if (d2 < MOUSE_DIST * MOUSE_DIST) {
            const alpha = (1 - Math.sqrt(d2) / MOUSE_DIST) * 0.55;
            ctx.strokeStyle = "rgba(17,205,239," + alpha.toFixed(3) + ")";
            ctx.beginPath();
            ctx.moveTo(a.x, a.y);
            ctx.lineTo(mouse.x, mouse.y);
            ctx.stroke();
          }
        }
      }

      // Node dots
      for (let i = 0; i < nodes.length; i++) {
        const n = nodes[i];
        ctx.fillStyle = "rgba(190,205,255,0.85)";
        ctx.beginPath();
        ctx.arc(n.x, n.y, n.r, 0, Math.PI * 2);
        ctx.fill();
      }

      raf = requestAnimationFrame(draw);
    };

    const reduced =
      typeof window.matchMedia === "function" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    if (reduced) {
      draw();
    } else {
      raf = requestAnimationFrame(draw);
    }

    return () => {
      if (raf) cancelAnimationFrame(raf);
      window.removeEventListener("resize", onResize);
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseleave", onLeave);
    };
  }, []);

  return (
    <canvas ref={canvasRef} className="jv-modern-neural" aria-hidden="true" />
  );
});

const BrandDecor = () => (
  <svg
    className="jv-modern-decor"
    viewBox="0 0 600 600"
    aria-hidden="true"
    preserveAspectRatio="xMidYMid slice"
  >
    <defs>
      <linearGradient id="jvDecorA" x1="0" x2="1" y1="0" y2="1">
        <stop offset="0%" stopColor="#5e72e4" stopOpacity="0.9" />
        <stop offset="100%" stopColor="#11cdef" stopOpacity="0.6" />
      </linearGradient>
      <linearGradient id="jvDecorB" x1="0" x2="1" y1="1" y2="0">
        <stop offset="0%" stopColor="#825ee4" stopOpacity="0.7" />
        <stop offset="100%" stopColor="#f5365c" stopOpacity="0.35" />
      </linearGradient>
      <radialGradient id="jvDecorGlow" cx="50%" cy="50%" r="50%">
        <stop offset="0%" stopColor="#ffffff" stopOpacity="0.18" />
        <stop offset="100%" stopColor="#ffffff" stopOpacity="0" />
      </radialGradient>
    </defs>
    <circle cx="120" cy="120" r="200" fill="url(#jvDecorA)" opacity="0.40" />
    <circle cx="500" cy="480" r="240" fill="url(#jvDecorB)" opacity="0.30" />
    <circle cx="420" cy="120" r="90" fill="url(#jvDecorGlow)" />
  </svg>
);

const BrandPane = ({ companyName, langData }) => {
  const tagline =
    _.get(langData, "SecAuthentication_Tagline") ||
    "Welcome back. Sign in to continue managing your operations with Jarvis.";
  return (
    <div className="jv-modern-brand">
      <BrandDecor />
      <div className="jv-modern-brand-overlay" />
      <div className="jv-modern-brand-inner">
        <div className="jv-modern-brand-top">
          <Link to="/" className="jv-modern-wordmark-link" title="Jarvis AI">
            <AiEmblem />
            <span className="jv-modern-wordmark-text">
              <span className="jv-modern-wordmark-row">
                <span className="jv-modern-wordmark">JARVIS</span>
              </span>
              {companyName && (
                <span className="jv-modern-wordmark-company">
                  {companyName}
                </span>
              )}
            </span>
          </Link>
        </div>
        <div className="jv-modern-headline">
          <div className="jv-modern-headline-text">
            <h1>
              {_.get(langData, "SecAuthentication_Title") || "Sign in to Jarvis"}
            </h1>
            <p>{tagline}</p>
          </div>
          <FeatureList langData={langData} />
          <SsoCallout langData={langData} />
          <div className="jv-modern-signed-status" aria-live="polite">
            <span className="jv-modern-signed-status-dot" aria-hidden="true" />
            <span>
              {_.get(langData, "SecAuthentication_SignedInStatus") ||
                "Signed in successfully — verify your details and continue."}
            </span>
          </div>
        </div>
        <div className="jv-modern-brand-footer">
          &copy; {new Date().getFullYear()} {companyName || "Jarvis"} &middot;{" "}
          {_.get(langData, "SecAuthentication_BrandFooterSecure") ||
            "Protected by enterprise-grade security"}
        </div>
      </div>
    </div>
  );
};

// Modern post-login user info — replicates SignInUserInfo logic without modifying it
const ModernUserInfoBase = (props) => {
  const {
    data,
    langData,
    isInternalSignIn,
    toastDismissAll,
  } = props;
  const [concurrentLogins, setConcurrentLogins] = useState(
    props.concurrentLogins || []
  );

  const {
    Gender,
    Fullname,
    Description,
    LastEventLoginDateTime,
    LeftTimeToChangePassword,
    UserMustLoginWithDomainController,
    LastEventLoginIpAddress,
    UserImageBase64,
    JAToken,
    EventLoginSpecificLoginToken,
    UserId,
    PatronId,
    VTPatronId,
  } = data.Data;
  const fullname = Fullname?.trim();
  const userImageBase64 = UserImageBase64
    ? UserImageBase64
    : require(`assets/images/${
        Gender === "PubGender_Female" ? "female" : "male"
      }User.png`);

  const encryptedData = _.get(data.ExtraInfo, "EncryptedData") ?? {};

  const getHandoffToken = async (fullToken) => {
    // M5: swap the full session token for a short-lived one so it isn't left in the URL/logs/Referer.
    // Falls back to the full token if the endpoint is unreachable, so sign-in never breaks.
    try {
      const r = await fetch("/JWebCore/JarvisAuthInvestigator/HandoffToken", {
        headers: { Authorization: `Bearer ${fullToken}` },
      });
      if (r.ok) {
        const j = await r.json();
        if (j && j.token) return j.token;
      }
    } catch (e) {}
    return fullToken;
  };
  const handleClick = async () => {
    const queryInfo = queryString.parse(props.location?.search);
    const jarvisIntegratedSystem = queryInfo?.jarvisIntegratedSystem
      ? queryInfo.jarvisIntegratedSystem
      : true;

    const authCallBackUrl = queryInfo.authCallBackUrl;
    if (!isNullOrWhiteSpace(authCallBackUrl)) {
      const userId = _.get(data.Data, "UserId");
      const eventLoginId = _.get(data.Data, "EventLoginId");
      const tokenExpiryUTC = _.get(data.ExtraInfo, "TokenExpiryUTC");
      const queryStr = `userId=${userId}&eventLoginId=${eventLoginId}&encryptedAuthToken=${encodeURIComponent(
        encryptedData
      )}&tokenExpiryUTC=${encodeURIComponent(tokenExpiryUTC)}`;
      window.location.href = `${authCallBackUrl}?${queryStr}`;
      return;
    }

    let isReloadPageNeeded = localStorage.getItem("isReloadPageNeeded");

    if (jarvisIntegratedSystem === true) {
      const prevUsername = getPrevUsername();
      if (data.username?.toLowerCase() !== prevUsername?.toLowerCase()) {
        setCompany({});
        setSubSystem({});
        isReloadPageNeeded = true;
      }

      props.userSignIn({
        Gender,
        Fullname,
        UserDescription: Description,
        LastEventLoginDateTime,
        LeftTimeToChangePassword,
        UserMustLoginWithDomainController,
        LastEventLoginIpAddress,
        UserImageBase64: userImageBase64,
        JAToken,
        username: data.username,
        EventLoginSpecificLoginToken,
        UserId,
        PatronId,
        VTPatronId,
      });

      if (isInternalSignIn === false || isTrue(isReloadPageNeeded) === true) {
        localStorage.removeItem("isReloadPageNeeded");
        try {
          props.history.push("/");
        } catch {
          window.location.href = "/";
        }
      } else {
        if (props.onClose) props.onClose();
        if (toastDismissAll) toastDismissAll();
      }
    } else {
      if (!queryInfo.systemUrl) {
        notify({
          type: "warning",
          message: `${_.get(langData, "SystemUrl_IsNull")} (${
            queryInfo.systemKey
          })`,
        });
        return;
      }
      if (isInternalSignIn === false) {
        const query = `jAToken=${await getHandoffToken(JAToken)}&systemKey=${
          queryInfo.systemKey
        }&systemUrl=${encodeURIComponent(
          queryInfo.systemUrl
        )}&returnSemiPath=${encodeURIComponent(
          queryInfo.returnSemiPath
        )}&encryptedData=${encodeURIComponent(encryptedData)}`;
        window.location.href = `${queryInfo.systemUrl}/JWebCore/JarvisAuthInvestigator/RedirectTo?${query}`;
      } else if (toastDismissAll) toastDismissAll();
    }
  };

  const onLoadConcurrentLogins = (d) => setConcurrentLogins(d ?? []);

  return (
    <>
      <div className="jv-modern-userinfo">
        <div className="jv-modern-avatar">
          <img src={userImageBase64} alt="User Avatar" title={fullname || Description} />
        </div>
        <div className="jv-modern-welcome">
          {`${_.get(langData, "SecAuthentication_WelcomeMessage_Title") || "Welcome"} ${
            _.get(langData, "SecAuthentication_WelcomeMessage") || ""
          }`}
        </div>
        {fullname && <div className="jv-modern-fullname">{fullname}</div>}
        {Description && <div className="jv-modern-description">{Description}</div>}

        <div className="jv-modern-info-grid">
          <div className="jv-modern-info-tile">
            <div className="jv-modern-info-label">
              {_.get(langData, "LastLoginDate") || "Last login"}
            </div>
            <div className="jv-modern-info-value ltr">
              {LastEventLoginDateTime || "—"}
            </div>
          </div>
          <div className="jv-modern-info-tile">
            <div className="jv-modern-info-label">
              {_.get(langData, "LastLoginIPAddress") || "IP address"}
            </div>
            <div className="jv-modern-info-value ltr">
              {LastEventLoginIpAddress || "—"}
            </div>
          </div>
        </div>

        {LeftTimeToChangePassword && (
          <div className="jv-modern-warn">
            {`${_.get(langData, "LeftTimeToChangePassword") || "Password expires in"}: ${LeftTimeToChangePassword}`}
          </div>
        )}

        <button
          type="button"
          className="jv-modern-continue"
          onClick={handleClick}
        >
          {_.get(langData, "Continue") || "Continue"}
        </button>
      </div>

      {concurrentLogins.length > 0 && (
        <div style={{ marginTop: 22 }}>
          <ConcurrentLogin
            concurrentLogins={concurrentLogins}
            onLoadConcurrentLogins={onLoadConcurrentLogins}
            isInternalSignIn={isInternalSignIn}
          />
        </div>
      )}
    </>
  );
};

const ModernUserInfo = connect(
  ({ auth }) => ({ authUser: auth.authUser }),
  { userSignIn }
)(ModernUserInfoBase);

const AccountModern = (props) => {
  const {
    isInternalSignIn,
    langData,
    configs,
    loginResultInfo,
    concurrentLogins,
    onLoadConcurrentLogins,
    _SetLoginResultInfo,
  } = props;

  // Drive the success transition. When loginResultInfo first appears we mount
  // the user-info card immediately (with its own enter animation) and, on the
  // next frame, add `is-success` to the shell so the flex transition kicks in
  // to shift the brand pane to the left.
  const [shellSuccess, setShellSuccess] = useState(!!loginResultInfo);
  useEffect(() => {
    if (loginResultInfo && !shellSuccess) {
      const id = requestAnimationFrame(() => setShellSuccess(true));
      return () => cancelAnimationFrame(id);
    }
    if (!loginResultInfo && shellSuccess) setShellSuccess(false);
  }, [loginResultInfo, shellSuccess]);

  const showConcurrent =
    !loginResultInfo && concurrentLogins && concurrentLogins.length > 0;

  const rootClass =
    "jv-modern-root" + (loginResultInfo ? " is-success" : "");
  const shellClass =
    "jv-modern-shell" + (shellSuccess ? " is-success" : "");
  const cardClass =
    "jv-modern-card" +
    (loginResultInfo || showConcurrent ? " is-wide" : "") +
    (loginResultInfo ? " jv-anim-slide-in-right" : "");

  return (
    <div className={rootClass}>
      <ModernStyles />
      <NeuralBackground />
      <div className={shellClass}>
        <BrandPane
          companyName={_.get(configs, "PubCompanyName")}
          langData={langData}
        />
        <div className="jv-modern-form-pane">
          <div className={cardClass} key={loginResultInfo ? "success" : "form"}>
            {loginResultInfo ? (
              <ModernUserInfo
                langData={langData}
                data={loginResultInfo}
                isInternalSignIn={isInternalSignIn}
                concurrentLogins={concurrentLogins}
                {...props}
              />
            ) : showConcurrent ? (
              <ConcurrentLogin
                concurrentLogins={concurrentLogins}
                onLoadConcurrentLogins={onLoadConcurrentLogins}
                isBackToLoginNeeded={true}
              />
            ) : (
              <>
                <div className="jv-modern-card-head">
                  {isInternalSignIn === false && (
                    <div className="jv-modern-lang">
                      <LanguageSwitcher
                        withBorder={true}
                        accessMustBeChecked={false}
                        {...props}
                      />
                    </div>
                  )}
                  <div className="jv-modern-card-headline">
                    <ShieldIcon size={26} />
                    <div className="jv-modern-card-title">
                      {_.get(langData, "SecAuthentication_Title") || "Sign in"}
                    </div>
                  </div>
                  <p className="jv-modern-card-subtitle">
                    {_.get(langData, "SecAuthentication_SubTitle") ||
                      "Enter your credentials to access your account."}
                  </p>
                  <SecureBadge langData={langData} />
                </div>
                <FormContainer
                  langData={langData}
                  configs={configs}
                  isInternalSignIn={isInternalSignIn}
                  _SetLoginResultInfo={_SetLoginResultInfo}
                  onLoadConcurrentLogins={onLoadConcurrentLogins}
                  {...props}
                />
                <TrustStrip langData={langData} />
                <div className="jv-modern-legal">
                  {_.get(langData, "SecAuthentication_LegalNotice") ||
                    "Access is monitored and logged. Unauthorized use is prohibited."}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AccountModern;
